/* eslint-disable no-nested-ternary */
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/16/solid';
import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import { useDialogState } from '../../hooks/useDialogState';
import { ContactHandleSchema } from '../../services/types/coverage-types';
import { MakeCallModal } from '../common/calls/make-call-modal';
import { SourceIndicator } from '../police/police-details/SourceIndicator';
import { ContactHandleDisplay } from '../treatment/contact-handles/ContactHandleDisplay';
import { ContactHandleForm } from '../treatment/contact-handles/ContactHandleForm';
import { Button } from './catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './catalyst/dialog';

interface ContactHandlesListProps {
  handles: ContactHandleSchema[];
  onUpdate: (handles: ContactHandleSchema[]) => Promise<void>;
  title?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  readOnly?: boolean;
  inline?: boolean;
  enableCallIntegration?: boolean;
}

// Helper function to get the icon for a contact type
const getTypeIcon = (type: string) => {
  const iconClass = 'h-4 w-4 text-gray-400';
  switch (type?.toUpperCase()) {
    case 'PHONE':
      return <PhoneIcon className={iconClass} />;
    case 'EMAIL':
      return <EnvelopeIcon className={iconClass} />;
    case 'WEBSITE':
    case 'URL':
      return <GlobeAltIcon className={iconClass} />;
    case 'FAX':
      return <DocumentTextIcon className={iconClass} />;
    case 'MAILING_ADDRESS':
      return <BuildingOffice2Icon className={iconClass} />;
    default:
      return <DocumentTextIcon className={iconClass} />;
  }
};

interface EmptyContactStateProps {
  onAddClick: () => void;
  title?: string;
  description?: string;
}

const EmptyContactState = ({
  onAddClick,
  title = 'No contacts',
  description = 'Get started by adding contact information.',
}: EmptyContactStateProps) => {
  return (
    <div className="text-center bg-white p-6">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-50">
        <PhoneIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
      </div>
      <h3 className="mt-3 text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-sm text-gray-500">{description}</p>
      <div className="mt-4">
        <button
          type="button"
          onClick={onAddClick}
          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Add contact
        </button>
      </div>
    </div>
  );
};

export const ContactHandlesList = ({
  handles,
  onUpdate,
  title = 'Contacts',
  emptyStateTitle,
  emptyStateDescription,
  readOnly = false,
  inline = false,
  enableCallIntegration = false,
}: ContactHandlesListProps) => {
  const addDialog = useDialogState();
  const editDialog = useDialogState();
  const deleteDialog = useDialogState();

  const [currentHandleIndex, setCurrentHandleIndex] = useState<number | null>(
    null,
  );
  const [contactHandles, setContactHandles] = useState<ContactHandleSchema[]>(
    handles || [],
  );
  const [formState, setFormState] = useState<ContactHandleSchema>({
    value: '',
    handle_type: 'PHONE',
    name: '',
  });
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');
  const [selectedContactName, setSelectedContactName] = useState<string>('');

  // Keep local state in sync with incoming props (e.g., after refresh or form hydration)
  useEffect(() => {
    setContactHandles(handles || []);
  }, [handles]);

  const openAddDialog = () => {
    setFormState({
      value: '',
      handle_type: 'PHONE',
      name: '',
    });
    addDialog.openDialog();
  };

  const openEditDialog = (handleIndex: number) => {
    const handle = contactHandles[handleIndex];
    setCurrentHandleIndex(handleIndex);
    setFormState({
      value: handle.value,
      handle_type: handle.handle_type,
      name: handle.name || '',
      sources: handle.sources,
    });
    editDialog.openDialog();
  };

  const openDeleteDialog = (handleIndex: number) => {
    setCurrentHandleIndex(handleIndex);
    deleteDialog.openDialog();
  };

  const saveToServer = async (updatedHandles: ContactHandleSchema[]) => {
    try {
      await onUpdate(updatedHandles);
      setContactHandles(updatedHandles);
    } catch (error) {
      console.error('Failed to update contact handles:', error);
    }
  };

  const handleAddContactHandle = () => {
    const updatedHandles = [...contactHandles, formState];
    saveToServer(updatedHandles);
    addDialog.closeDialog();
  };

  const handleUpdateContactHandle = () => {
    if (currentHandleIndex !== null) {
      const updatedHandles = [...contactHandles];
      updatedHandles[currentHandleIndex] = formState;
      saveToServer(updatedHandles);
      editDialog.closeDialog();
    }
  };

  const handleDeleteContactHandle = () => {
    if (currentHandleIndex !== null) {
      const updatedHandles = contactHandles.filter(
        (_, index) => index !== currentHandleIndex,
      );
      saveToServer(updatedHandles);
      deleteDialog.closeDialog();
    }
  };

  const handleFormChange = (updatedForm: ContactHandleSchema) => {
    setFormState(updatedForm);
  };

  const toggleFilter = (filterType: string) => {
    const normalized = (filterType || '').toUpperCase();
    setActiveFilters((prev) => {
      if (prev.includes(normalized)) {
        return prev.filter((type) => type !== normalized);
      }
      return [...prev, normalized];
    });
  };

  const resetFilters = () => {
    setActiveFilters([]);
  };

  const handleCallClick = (phoneNumber: string, contactName?: string) => {
    setSelectedPhoneNumber(phoneNumber);
    setSelectedContactName(contactName || '');
    setIsCallModalOpen(true);
  };

  const handleCloseCallModal = () => {
    setIsCallModalOpen(false);
    setSelectedPhoneNumber('');
    setSelectedContactName('');
  };

  // Normalize and validate handles to avoid false empty states
  const normalizedHandles = useMemo(() => {
    return (contactHandles || [])
      .map((h) => ({
        ...h,
        handle_type: (h?.handle_type || '').toUpperCase(),
        value: (h?.value || '').trim(),
      }))
      .filter((h) => h.value.length > 0);
  }, [contactHandles]);

  const filteredHandles = normalizedHandles.filter(
    (handle) =>
      activeFilters.length === 0 || activeFilters.includes(handle.handle_type),
  );

  // Get unique handle types for filter buttons
  const availableTypes = Array.from(
    new Set(normalizedHandles.map((h) => h.handle_type)),
  );

  return (
    <div
      className={
        inline && !title
          ? ''
          : 'overflow-hidden bg-white sm:rounded-lg sm:shadow-sm'
      }
    >
      {!inline || title ? (
        <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
          <div className="-mt-1 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
            <div className="mt-1 ml-4">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            </div>
            {!readOnly && (
              <div className="mt-1 ml-4 shrink-0">
                <Button plain type="button" onClick={openAddDialog}>
                  <PlusIcon className="h-3.5 w-3.5 mr-1" />
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {availableTypes.length > 1 && (
        <div
          className={`px-4 py-2 ${!inline || title ? 'border-b border-gray-200' : ''}`}
        >
          <div className="flex items-center space-x-2 text-xs">
            <div className="flex flex-wrap gap-1">
              {availableTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleFilter(type)}
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                    activeFilters.includes(type)
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {getTypeIcon(type)}
                  <span className="ml-1 capitalize">{type.toLowerCase()}</span>
                </button>
              ))}
            </div>
            {activeFilters.length > 0 && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}

      {filteredHandles.length === 0 ? (
        activeFilters.length > 0 ? (
          <div className="text-center bg-white p-6">
            <p className="text-sm text-gray-500">
              No contacts match the selected filters.
            </p>
            <div className="mt-3">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
              >
                Clear filters
              </button>
            </div>
          </div>
        ) : !readOnly ? (
          <EmptyContactState
            onAddClick={openAddDialog}
            title={emptyStateTitle}
            description={emptyStateDescription}
          />
        ) : (
          <div className="text-center bg-white p-6">
            <p className="text-sm text-gray-500">
              No contact information available.
            </p>
          </div>
        )
      ) : (
        <div className="max-h-[270px] overflow-y-auto">
          <ul className="divide-y divide-gray-100">
            {filteredHandles.map((handle, index) => (
              <li key={index} className="block group">
                <div className="flex flex-row items-center justify-between p-3">
                  <div className="flex flex-row items-center space-x-3">
                    {enableCallIntegration && handle.handle_type === 'PHONE' ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleCallClick(handle.value, handle.name)
                        }
                        aria-label="Make a call"
                        className="text-green-600 hover:text-green-700 transition-colors cursor-pointer"
                        title="Make a call"
                      >
                        <PhoneIcon className="h-4 w-4" />
                      </button>
                    ) : (
                      getTypeIcon(handle.handle_type)
                    )}
                    <div className="flex flex-col">
                      {enableCallIntegration &&
                      handle.handle_type === 'PHONE' ? (
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCallClick(handle.value, handle.name);
                          }}
                          className="font-medium text-gray-900 leading-tight hover:text-green-600 transition-colors cursor-pointer"
                          title="Make a call"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleCallClick(handle.value, handle.name);
                            }
                          }}
                        >
                          <ContactHandleDisplay
                            handle={handle as any}
                            displayMode="valueOnly"
                            showType={false}
                            compact
                          />
                        </div>
                      ) : (
                        <div className="font-medium text-gray-900 leading-tight">
                          <ContactHandleDisplay
                            handle={handle as any}
                            displayMode="valueOnly"
                            showType={false}
                            compact
                          />
                        </div>
                      )}
                      {handle.name && (
                        <div className="text-xs text-gray-500">
                          {handle.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-x-1.5">
                    {handle.sources && handle.sources.length > 0 && (
                      <SourceIndicator sources={handle.sources} />
                    )}
                    {!readOnly && (
                      <div className="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => openEditDialog(index)}
                          aria-label="Edit contact"
                          className="h-7 w-7 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-gray-100 rounded transition-colors"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteDialog(index)}
                          aria-label="Delete contact"
                          className="h-7 w-7 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-gray-100 rounded transition-colors"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!readOnly && (
        <>
          {/* Add Contact Dialog */}
          <Dialog open={addDialog.isOpen} onClose={addDialog.closeDialog}>
            <DialogTitle>Add Contact Information</DialogTitle>
            <DialogDescription>Add new contact information.</DialogDescription>
            <DialogBody>
              <ContactHandleForm
                value={formState as any}
                onChange={handleFormChange}
              />
              <DialogActions>
                <Button onClick={addDialog.closeDialog}>Cancel</Button>
                <Button color="blue" onClick={handleAddContactHandle}>
                  Add Contact
                </Button>
              </DialogActions>
            </DialogBody>
          </Dialog>

          {/* Edit Contact Dialog */}
          <Dialog open={editDialog.isOpen} onClose={editDialog.closeDialog}>
            <DialogTitle>Edit Contact Information</DialogTitle>
            <DialogDescription>
              Update contact information details.
            </DialogDescription>
            <DialogBody>
              <ContactHandleForm
                value={formState as any}
                onChange={handleFormChange}
              />
              <DialogActions>
                <Button onClick={editDialog.closeDialog}>Cancel</Button>
                <Button color="blue" onClick={handleUpdateContactHandle}>
                  Update Contact
                </Button>
              </DialogActions>
            </DialogBody>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialog.isOpen} onClose={deleteDialog.closeDialog}>
            <DialogTitle>Delete Contact Information</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this contact information?
            </DialogDescription>
            <DialogBody>
              <DialogActions>
                <Button onClick={deleteDialog.closeDialog}>Cancel</Button>
                <Button color="red" onClick={handleDeleteContactHandle}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </Dialog>
        </>
      )}

      {/* Make Call Modal */}
      {enableCallIntegration && (
        <MakeCallModal
          isOpen={isCallModalOpen}
          onClose={handleCloseCallModal}
          initialPhoneNumber={selectedPhoneNumber}
          initialContactLabel={selectedContactName}
          source="contact_handles_list"
        />
      )}
    </div>
  );
};
